/**
 * Lightweight SVG doughnut chart. No dependencies.
 *
 * Everything — ring and center label — lives inside the SVG viewBox, so it
 * all scales together with the host element. No HTML overlays, no breakpoint
 * juggling. Set the size with utility classes on the element itself.
 *
 * The host gets display:block and aspect-ratio:1 via CSS, so space is
 * reserved before the component upgrades — no layout shift.
 *
 * On mount the used segment and center percentage tween from 0 to their
 * target via requestAnimationFrame (ease-out cubic). Respects
 * prefers-reduced-motion.
 *
 * <imos-doughnut-chart class="w-56" used="3" total="10" used-label="used">
 */
const SVG_NS = 'http://www.w3.org/2000/svg';

// r chosen so the circumference is ~100 → dasharray works as percentages
const RADIUS = 15.915;
const STROKE_WIDTH = 5;
// Gap width between segments, in viewBox units.
const GAP = 1;
// Path distance of the 12 o'clock point (circle path starts at 3 o'clock).
const TOP_OFFSET = 75;
const ANIM_MS = 800;

let instanceCount = 0;

export class ImosDoughnutChart extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['used', 'total', 'used-label'];
  }

  private readonly maskId = `imos-doughnut-mask-${instanceCount++}`;
  private usedArc: SVGCircleElement | null = null;
  private remainingArc: SVGCircleElement | null = null;
  private junctionGap: SVGRectElement | null = null;
  private percentText: SVGTextElement | null = null;
  private currentPct = 0;
  private rafId: number | null = null;

  connectedCallback(): void {
    this.render();
    this.animateTo(this.targetPct());
  }

  disconnectedCallback(): void {
    this.cancelAnim();
  }

  attributeChangedCallback(name: string): void {
    if (!this.isConnected || !this.percentText) {
      return;
    }
    if (name === 'used-label' || name === 'total') {
      // Structural change: aria-label, label presence, ring visibility.
      this.render();
    }
    this.animateTo(this.targetPct());
  }

  private get used(): number {
    return Math.max(0, Number(this.getAttribute('used')) || 0);
  }

  private get total(): number {
    return Math.max(0, Number(this.getAttribute('total')) || 0);
  }

  private targetPct(): number {
    const total = this.total;
    if (total <= 0) {
      return 0;
    }
    const used = Math.min(this.used, total);
    return (used / total) * 100;
  }

  private cancelAnim(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private animateTo(target: number): void {
    this.cancelAnim();
    const start = this.currentPct;
    if (start === target) {
      this.setProgress(target);
      return;
    }
    const reduce =
      typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      this.currentPct = target;
      this.setProgress(target);
      return;
    }
    const t0 = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - t0) / ANIM_MS, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const pct = start + (target - start) * eased;
      this.currentPct = pct;
      this.setProgress(pct);
      this.rafId = t < 1 ? requestAnimationFrame(tick) : null;
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private setProgress(pct: number): void {
    const clamped = Math.max(0, Math.min(100, pct));
    const remPct = 100 - clamped;
    if (this.usedArc) {
      this.usedArc.setAttribute('stroke-dasharray', `${clamped} ${100 - clamped}`);
      this.usedArc.setAttribute('stroke-dashoffset', String(-TOP_OFFSET));
    }
    if (this.remainingArc) {
      this.remainingArc.setAttribute('stroke-dasharray', `${remPct} ${100 - remPct}`);
      this.remainingArc.setAttribute('stroke-dashoffset', String(-(clamped + TOP_OFFSET)));
    }
    if (this.junctionGap) {
      this.applyGapTransform(this.junctionGap, clamped);
    }
    if (this.percentText) {
      this.percentText.textContent = `${Math.round(clamped)}%`;
    }
  }

  private applyGapTransform(rect: SVGRectElement, positionPct: number): void {
    // -90° so position 0 sits at 12 o'clock, matching the ring.
    const angleDeg = (positionPct / 100) * 360 - 90;
    const angleRad = (angleDeg * Math.PI) / 180;
    const jx = 20 + RADIUS * Math.cos(angleRad);
    const jy = 20 + RADIUS * Math.sin(angleRad);
    rect.setAttribute('transform', `translate(${jx} ${jy}) rotate(${angleDeg})`);
  }

  private makeArc(cls: string): SVGCircleElement {
    const c = document.createElementNS(SVG_NS, 'circle');
    c.setAttribute('cx', '20');
    c.setAttribute('cy', '20');
    c.setAttribute('r', String(RADIUS));
    c.setAttribute('fill', 'none');
    c.setAttribute('stroke-width', String(STROKE_WIDTH));
    c.setAttribute('class', `${cls} stroke-current`);
    return c;
  }

  /**
   * A straight, constant-width slot punched through the ring. Constant width
   * → the two gap edges stay parallel.
   */
  private makeGapRect(): SVGRectElement {
    const radialLength = STROKE_WIDTH + 4;
    const rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('x', String(-radialLength / 2));
    rect.setAttribute('y', String(-GAP / 2));
    rect.setAttribute('width', String(radialLength));
    rect.setAttribute('height', String(GAP));
    rect.setAttribute('fill', 'black');
    return rect;
  }

  private centerText(
    y: number,
    fontSize: number,
    cls: string,
    content: string,
  ): SVGTextElement {
    const text = document.createElementNS(SVG_NS, 'text');
    text.setAttribute('x', '20');
    text.setAttribute('y', String(y));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('font-size', String(fontSize));
    text.setAttribute('class', `${cls} fill-current`);
    text.textContent = content;
    return text;
  }

  private render(): void {
    this.cancelAnim();
    this.currentPct = 0;

    const total = this.total;
    const used = this.used;
    const remaining = Math.max(total - used, 0);

    const svg = document.createElementNS(SVG_NS, 'svg');
    // Tight viewBox around the ring's outer edge (cx/cy 20, r 15.915, sw 5).
    svg.setAttribute('viewBox', '1.585 1.585 36.83 36.83');
    svg.setAttribute('class', 'block w-full h-full');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `${remaining} of ${total} remaining`);

    const hasRing = total > 0 || used > 0;
    this.usedArc = null;
    this.remainingArc = null;
    this.junctionGap = null;

    // Both arcs and both gaps always present (when ring shown) so the
    // animation can tween attributes without DOM mutation.
    if (hasRing) {
      const defs = document.createElementNS(SVG_NS, 'defs');
      const mask = document.createElementNS(SVG_NS, 'mask');
      mask.setAttribute('id', this.maskId);

      const bg = document.createElementNS(SVG_NS, 'rect');
      bg.setAttribute('x', '0');
      bg.setAttribute('y', '0');
      bg.setAttribute('width', '40');
      bg.setAttribute('height', '40');
      bg.setAttribute('fill', 'white');

      const topGap = this.makeGapRect();
      this.applyGapTransform(topGap, 0);

      const junctionGap = this.makeGapRect();
      this.applyGapTransform(junctionGap, 0);
      this.junctionGap = junctionGap;

      mask.append(bg, topGap, junctionGap);
      defs.append(mask);
      svg.append(defs);

      const group = document.createElementNS(SVG_NS, 'g');
      group.setAttribute('mask', `url(#${this.maskId})`);

      this.usedArc = this.makeArc('text-highlight');
      this.remainingArc = this.makeArc('text-gray-250');
      group.append(this.usedArc, this.remainingArc);
      svg.append(group);
    }

    const usedLabel = this.getAttribute('used-label');
    this.percentText = this.centerText(
      usedLabel ? 19 : 22,
      6.5,
      'font-bold text-highlight',
      '0%',
    );
    svg.append(this.percentText);
    if (usedLabel) {
      svg.append(this.centerText(24.5, 2.8, 'text-gray-300', usedLabel));
    }

    // Start at 0 so animateTo can tween up to the target.
    this.setProgress(0);
    this.replaceChildren(svg);
  }
}

customElements.define('imos-doughnut-chart', ImosDoughnutChart);
